import debug from 'debug';
import { createMiddleware } from 'redux-beacon';
import get from 'lodash/get';
import { actions } from 'app-webtech-core/actions';
import { actionCreator } from 'app-webtech-core/actions/actionCreator';
import {
  BOOTSTRAP_RESTART_REQUEST,
  SET_SESSION_TOKEN,
  BUFFER_EVENTS,
  SESSION_TOKEN_REFRESHED,
} from 'app-webtech-core/modules/application/actions/types';
import {
  SESSION_RESET,
  TRACK_EVENTS,
} from 'app-webtech-core/modules/playbackSession/actions/types';
import { SET_IS_VIDEO_REQUESTED } from 'app-webtech-core/modules/player/actions/types';
import { isPerformanceEnabledSelector } from 'app-webtech-core/selectors/isPerformanceEnabled';
import { applicationPropertiesSelector } from 'app-webtech-core/modules/application/selectors/properties';
import { applicationEventDefinitionsMap } from 'app-webtech-core/modules/application/beacons';
import { guideEventDefinitionsMap } from 'app-webtech-core/modules/guide/beacons';
import { layoutEventDefinitionsMap } from 'app-webtech-core/modules/layout/beacons';
import { linearChannelsEventDefinitionsMap } from 'app-webtech-core/modules/linearChannels/beacons';
import { pairedDevicesEventDefinitionsMap } from 'app-webtech-core/modules/pairedDevices/beacons';
import { playbackEventDefinitionsMap } from 'app-webtech-core/modules/playbackSession/beacons';
import { playerEventDefinitionsMap } from 'app-webtech-core/modules/player/beacons';
import { podTrackerAdsDefinitionsMap } from 'app-webtech-core/modules/podTracker/ads/beacons';
import { userAccountEventDefinitionsMap } from 'app-webtech-core/modules/userAccount/beacons';
import { vodGuideEventDefinitionsMap } from 'app-webtech-core/modules/vodGuide/beacons';
import {
  refreshToken,
  bufferEvents as bufferEventsAction,
} from 'app-webtech-core/modules/application/actions';
import { sessionReset } from 'app-webtech-core/modules/playbackSession/actions';

import { POSTFIX_BEACON, TARGETS, SESSION_INACTIVITY_PERIOD } from './const';
import { multiTarget } from './multiTarget';
import { plutoTarget } from './plutoTarget';
import { msnTarget } from './msnTarget';

const log = debug('beacon:index');

const eventDefinitionsMap = {
  ...applicationEventDefinitionsMap,
  ...guideEventDefinitionsMap,
  ...layoutEventDefinitionsMap,
  ...linearChannelsEventDefinitionsMap,
  ...pairedDevicesEventDefinitionsMap,
  ...playbackEventDefinitionsMap,
  ...playerEventDefinitionsMap,
  ...podTrackerAdsDefinitionsMap,
  ...userAccountEventDefinitionsMap,
  ...vodGuideEventDefinitionsMap,
};

export const type2Beacon = (type) => type + POSTFIX_BEACON;

const addPostfix = (evDefMap) => {
  const resMap = {};
  Object.keys(evDefMap).forEach((key) => {
    resMap[type2Beacon(key)] = evDefMap[key];
  });
  return resMap;
};

const postfixMap = addPostfix(eventDefinitionsMap);

const targetMap = {
  [TARGETS.PLUTO]: plutoTarget,
  [TARGETS.MSN]: msnTarget,
};

const sessionResetMiddleware = ({ dispatch, getState }) => {
  let lastActionDate = Date.now();
  const byPassActions = [SET_SESSION_TOKEN, BOOTSTRAP_RESTART_REQUEST];
  return (next) => async (action) => {
    const now = Date.now();
    const state = getState();
    const applicationData = applicationPropertiesSelector(state);
    if (Object.keys(eventDefinitionsMap).includes(action.type)) {
      if (
        now - lastActionDate >
        get(applicationData, 'setup.restartThresholdMS', SESSION_INACTIVITY_PERIOD)
      ) {
        if (
          !byPassActions.includes(action.type) &&
          !applicationData.refreshingToken &&
          applicationData.launchState === 'APP_LOADED'
        ) {
          const oldSessionID = await dispatch(refreshToken({ lastActionDate }));
          if (oldSessionID) {
            dispatch(sessionReset({ action, oldSessionID }));
            lastActionDate = now;
            return next({ ...action, type: `${action.type}__MISS` });
          }
          next(action);
          dispatch(bufferEventsAction({ bufferEvents: false }));
          lastActionDate = now;
          return next({ ...action, type: `${action.type}__MISS` });
        }
      }
      lastActionDate = now;
    }
    return next(action);
  };
};

const sessionResetBufferMiddleware = ({ dispatch, getState }) => {
  const buffer = [];
  let setSessionTokenIndex = -1;
  let videoRequestIndex = -1;
  let isSessionReset = false;
  let shouldShiftEvents = false;

  return (next) => async (action) => {
    if (shouldShiftEvents) {
      if (buffer.length > 0) {
        shouldShiftEvents = false;
        if (videoRequestIndex !== -1) {
          buffer.splice(videoRequestIndex, 2);
        }
        const initAction = [...buffer.splice(buffer.length - 1)];
        const allActions = [...initAction, ...buffer];
        while (allActions.length > 0) {
          if (allActions.length === 1) {
            return next(allActions.shift());
          }
          next(allActions.shift());
        }
      }
    }
    if (action.type === SESSION_RESET) {
      dispatch(bufferEventsAction({ bufferEvents: false }));
      const initAction = buffer.splice(buffer.length - 1);
      const afterResetActions = [...buffer.splice(setSessionTokenIndex), action, ...initAction];
      const allActions = [...afterResetActions, ...buffer];
      while (allActions.length > 0) {
        if (allActions.length === 1) {
          return next(allActions.shift());
        }
        next(allActions.shift());
      }
    }
    if (getState().application.bufferEvents) {
      if (action.type === BUFFER_EVENTS) {
        if (!isSessionReset) {
          shouldShiftEvents = true;
        }
        return next(action);
      }
      if (action.type === SET_IS_VIDEO_REQUESTED) {
        videoRequestIndex = buffer.length;
        const startTrackingAction = actionCreator(TRACK_EVENTS);
        dispatch(
          startTrackingAction({
            trackChannelEvents: true,
          }),
        );
        dispatch(actions.guide.setPrevContentKind({ prevContentKind: null }));
      }
      if (action.type === SET_SESSION_TOKEN) {
        setSessionTokenIndex = buffer.length;
      }
      if (action.type === SESSION_TOKEN_REFRESHED) {
        isSessionReset = true;
      }
      buffer.push(action);
      // missing buffered actions
      return next({ ...action, type: `${action.type}__MISS` });
    }
    return next(action);
  };
};

const queueBeaconsMiddleware = (store) => {
  const queue = [];
  return (next) => (action) => {
    const beaconedAction = { ...action, type: type2Beacon(action.type) };
    const state = store.getState();
    const application = applicationPropertiesSelector(state);
    const isPerformanceEnabled = isPerformanceEnabledSelector(state);
    log('isPerformanceEnabled', isPerformanceEnabled);
    if (application.isConfigured && isPerformanceEnabled !== null) {
      if (!isPerformanceEnabled) {
        log('performance not enabled clearing saved actions');
        queue.length = 0;
        return next(action);
      }
      while (queue.length > 0) {
        next(queue.shift());
      }
      next(beaconedAction);
    } else {
      log('queueing beacon actions');
      queue.push(beaconedAction);
    }
    return next(action);
  };
};

export const beaconMiddlewares = [
  sessionResetMiddleware,
  sessionResetBufferMiddleware,
  queueBeaconsMiddleware,
  createMiddleware(postfixMap, multiTarget(targetMap)),
];
